// ../utils.ts
var isBrowser = () => typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.navigator !== "undefined";
var IS_BROWSER = isBrowser();
var AGENT = IS_BROWSER ? navigator.userAgent : "";
var IS_ANDROID = /android/i.test(AGENT);
var _a;
IS_BROWSER && // @ts-expect-error Typescript has not implemented userAgentData yet https://stackoverflow.com/a/71392474
/^Mac/i.test(((_a = navigator.userAgentData) == null ? void 0 : _a.platform) || navigator.platform);
var SUPPORTS_CONSTRUCTED_CSS = IS_BROWSER && window.CSSStyleSheet && document.adoptedStyleSheets;
var DISPLAY_BLOCK = ":host(:not([hidden])) { display: block }";
var SAFE_LABELLEDBY = `${IS_ANDROID ? "data" : "aria"}-labelledby`;
var UHTMLElement = typeof HTMLElement === "undefined" ? class {
} : HTMLElement;
var attr = (el, name, value) => {
  if (value === void 0) return el.getAttribute(name);
  if (value === null) el.removeAttribute(name);
  else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  return null;
};
var on = (el, ...rest) => {
  const [types, ...options] = rest;
  for (const type of types.split(" ")) el.addEventListener(type, ...options);
  return () => off(el, ...rest);
};
var off = (el, ...rest) => {
  const [types, ...options] = rest;
  for (const type of types.split(" ")) el.removeEventListener(type, ...options);
};
var attachStyle = (el, css) => {
  const root = el.shadowRoot || el.attachShadow({ mode: "open" });
  if (!root.querySelector("slot")) root.append(tag("slot"));
  if (!root.querySelector("style")) {
    if (!SUPPORTS_CONSTRUCTED_CSS) root.append(tag("style", null, css));
    else {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = [sheet];
    }
  }
  return root;
};
var onMutation = (el, callback, options) => {
  const observer = new MutationObserver((records) => {
    if (!isBrowser() || !el.isConnected) return cleanup();
    callback(el, records);
  });
  const cleanup = Object.assign(() => observer.disconnect(), {
    takeRecords: () => observer.takeRecords()
    // Expose takeRecords - useful if mutating a attribute that is observed
  });
  callback(el);
  observer.observe(el, options);
  return cleanup;
};
var getRoot = (node) => {
  var _a2;
  const root = ((_a2 = node.getRootNode) == null ? void 0 : _a2.call(node)) || node.ownerDocument;
  return root instanceof Document || root instanceof ShadowRoot ? root : document;
};
var useId = (el) => {
  if (!el || !IS_BROWSER) return null;
  if (!window.uElementsId) window.uElementsId = {};
  if (!el.id) {
    const node = el.nodeName.toLowerCase();
    if (!window.uElementsId[node]) window.uElementsId[node] = 1;
    el.id = `:${node}${window.uElementsId[node]++}`;
  }
  return el.id;
};
var tag = (tagName, attrs, content) => {
  const el = document.createElement(tagName);
  if (content) el.textContent = content;
  if (attrs) for (const [key, val] of Object.entries(attrs)) attr(el, key, val);
  return el;
};
var customElements = {
  define: (name, instance) => !isBrowser() || window.customElements.get(name) || window.customElements.define(name, instance)
};
var declarativeShadowRoot = (style, slot = "<slot></slot>") => `<template shadowrootmode="open">${slot}<style>${style}</style></template>`;

// u-tabs.ts
var ARIA_CONTROLS = "aria-controls";
var ARIA_DISABLED = "aria-disabled";
var ARIA_SELECTED = "aria-selected";
var TABINDEX = "tabindex";
var UHTMLTabsStyle = DISPLAY_BLOCK;
var UHTMLTabPanelStyle = DISPLAY_BLOCK;
var UHTMLTabStyle = "";
var UHTMLTabListStyle = `${DISPLAY_BLOCK}
::slotted([role="tab"]:not([hidden])) { display: inline-block; cursor: pointer }
::slotted([role="tab"][${ARIA_DISABLED}="true"]) { cursor: default }`;
var UHTMLTabsShadowRoot = declarativeShadowRoot(UHTMLTabsStyle);
var UHTMLTabListShadowRoot = declarativeShadowRoot(UHTMLTabListStyle);
var UHTMLTabShadowRoot = "";
var UHTMLTabPanelShadowRoot = declarativeShadowRoot(UHTMLTabPanelStyle);
var UHTMLTabsElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(this, UHTMLTabsStyle);
  }
  get tabList() {
    return this.querySelector(
      '[role="tablist"]:not(:scope [role="tabpanel"] [role="tablist"])'
    );
  }
  get selectedIndex() {
    const tabs = getTabs(this.tabList);
    return tabs.indexOf(findSelected(tabs));
  }
  set selectedIndex(index) {
    setSelected(this.tabs[index]);
  }
  get tabs() {
    return this.querySelectorAll(
      '[role="tab"]:not(:scope [role="tabpanel"] [role="tab"])'
    );
  }
  get panels() {
    return this.querySelectorAll(
      `[role="tabpanel"]:not(:scope [role="tabpanel"] [role="tabpanel"])`
    );
  }
};
var UHTMLTabListElement = class extends UHTMLElement {
  // Using underscore instead of private fields for backwards compatibility
  constructor() {
    super();
    attachStyle(this, UHTMLTabListStyle);
  }
  connectedCallback() {
    attr(this, "role", "tablist");
    on(this, "click keydown", this);
    this._umutate = onMutation(this, onMutations, {
      attributeFilter: ["id", "role", ARIA_CONTROLS, ARIA_SELECTED],
      // Needed if role="tab|tabpanel" and not u-tab|u-tabpanel is used
      attributes: true,
      childList: true,
      subtree: true
      // Needed to catch changes of attributes in children
    });
  }
  disconnectedCallback() {
    var _a2;
    off(this, "click keydown", this);
    (_a2 = this._umutate) == null ? void 0 : _a2.call(this);
    this._umutate = void 0;
  }
  handleEvent(event) {
    var _a2, _b, _c, _d;
    const { key, type, target } = event;
    const tabs = getTabs(this);
    const prev = tabs.findIndex((tab) => tab.contains(target));
    const isKeyClick = key === " " || key === "Enter";
    let next = prev;
    if (event.defaultPrevented || prev === -1) return;
    if (type === "click") setSelected(tabs[prev]);
    if (type === "keydown") {
      if (isKeyClick) {
        (_a2 = event.preventDefault) == null ? void 0 : _a2.call(event);
        const click = { bubbles: true, cancelable: true, composed: true };
        return tabs[prev].dispatchEvent(new MouseEvent("click", click));
      }
      if (key === "ArrowDown" || key === "ArrowRight")
        next = (prev + 1) % tabs.length;
      else if (key === "ArrowUp" || key === "ArrowLeft")
        next = (prev || tabs.length) - 1;
      else if (key === "End") next = tabs.length - 1;
      else if (key === "Home") next = 0;
      else if (key === "Tab" && !isSelected(tabs[prev])) {
        const selected = findSelected(tabs);
        if (selected) attr(selected, TABINDEX, "-1");
        return selected && setTimeout(() => attr(selected, TABINDEX, "0"));
      } else return;
      (_b = event.preventDefault) == null ? void 0 : _b.call(event);
      (_d = (_c = tabs[next]).focus) == null ? void 0 : _d.call(_c);
    }
  }
  get tabsElement() {
    return getTabsElement(this);
  }
  get tabs() {
    return this.querySelectorAll(':scope > [role="tab"]');
  }
  get selectedIndex() {
    const tabs = getTabs(this);
    return tabs.indexOf(findSelected(tabs));
  }
  set selectedIndex(index) {
    setSelected(this.tabs[index]);
  }
};
var UHTMLTabElement = class extends UHTMLElement {
  static get observedAttributes() {
    return ["id", ARIA_SELECTED, ARIA_CONTROLS];
  }
  connectedCallback() {
    var _a2;
    attr(this, "role", "tab");
    attr(this, ARIA_SELECTED, `${this.selected}`);
    attr(this, TABINDEX, this.selected ? "0" : "-1");
    if (!findSelected((_a2 = this.parentElement) == null ? void 0 : _a2.children)) setSelected(this);
  }
  attributeChangedCallback() {
    if (!SKIP_TABS_UPDATE.has(this.parentElement) && this.selected)
      setSelected(this);
  }
  get tabsElement() {
    return getTabsElement(this);
  }
  get tabList() {
    const tablist = this.parentElement;
    return tablist instanceof UHTMLTabListElement ? tablist : null;
  }
  get selected() {
    return isSelected(this);
  }
  set selected(value) {
    if (value) setSelected(this);
  }
  /** Retrieves the ordinal position of an tab in a tablist. */
  get index() {
    return Math.max(getTabs(this.parentElement).indexOf(this), 0);
  }
  get panel() {
    var _a2;
    const panels = (_a2 = getTabsElement(this)) == null ? void 0 : _a2.panels;
    const index = getTabs(this.parentElement).indexOf(this);
    return getPanel(this, panels == null ? void 0 : panels[index]);
  }
};
var UHTMLTabPanelElement = class extends UHTMLElement {
  constructor() {
    super();
    attachStyle(this, UHTMLTabPanelStyle);
  }
  connectedCallback() {
    attr(this, "role", "tabpanel");
    const tabs = new Set(this.tabs);
    const root = getTabsElement(this);
    const idx = root == null ? void 0 : root.tabs[Array.prototype.indexOf.call(root == null ? void 0 : root.panels, this)];
    if (idx && !idx.hasAttribute(ARIA_CONTROLS)) tabs.add(idx);
    const tab = findSelected(tabs) || tabs.values().next().value;
    skipTabsUpdate(tab, () => syncPanel(this, tab, !!tab && isSelected(tab)));
  }
  get tabsElement() {
    return getTabsElement(this);
  }
  get tabs() {
    return getRoot(this).querySelectorAll(
      `[${ARIA_CONTROLS}="${useId(this)}"]`
    );
  }
};
var onMutations = (self, records = []) => {
  let selected;
  const tabs = getTabs(self);
  for (const { target: el } of records) {
    const tab = el instanceof Element && attr(el, "role") === "tab";
    if (tab && isSelected(el)) selected = el;
  }
  if (!selected && !findSelected(tabs)) selected = tabs.find(isEnabled);
  setSelected(selected);
};
var syncPanel = (panel, tab, show = false) => {
  attr(panel, "aria-hidden", `${!show}`);
  attr(panel, "hidden", show ? null : "");
  attr(panel, TABINDEX, show ? "0" : null);
  if (tab) attr(tab, ARIA_CONTROLS, useId(panel));
  if (tab && show && isSelected(tab)) attr(panel, SAFE_LABELLEDBY, useId(tab));
};
var SKIP_TABS_UPDATE = /* @__PURE__ */ new Set();
var skipTabsUpdate = (tab, fn) => {
  var _a2;
  const tablist = tab == null ? void 0 : tab.parentElement;
  if (tablist) SKIP_TABS_UPDATE.add(tablist);
  fn();
  if (tablist) SKIP_TABS_UPDATE.delete(tablist);
  (_a2 = tablist == null ? void 0 : tablist._umutate) == null ? void 0 : _a2.takeRecords();
};
var isEnabled = (el) => attr(el, ARIA_DISABLED) !== "true";
var isSelected = (el) => attr(el, ARIA_SELECTED) === "true";
var findSelected = (elems = []) => {
  for (const el of elems) if (isSelected(el)) return el;
};
var getTabs = (tablist) => {
  const tabs = [];
  for (const el of (tablist == null ? void 0 : tablist.children) || [])
    if (el.getAttribute("role") === "tab") tabs.push(el);
  return tabs;
};
var getPanel = (tab, panel = null) => {
  const id = attr(tab, ARIA_CONTROLS);
  const el = id ? getRoot(tab).getElementById(id) : panel;
  return el;
};
var getTabsElement = (self) => {
  for (let el = self; el; el = el.parentNode || el.host)
    if (el instanceof UHTMLTabsElement) return el;
  return null;
};
var setSelected = (selected) => selected && isEnabled(selected) && skipTabsUpdate(selected, () => {
  var _a2;
  const tabs = getTabs(selected.parentElement);
  const panels = ((_a2 = getTabsElement(selected)) == null ? void 0 : _a2.panels) || [];
  const nextPanel = getPanel(selected, panels == null ? void 0 : panels[tabs.indexOf(selected)]);
  let idx = 0;
  for (const tab of tabs) {
    const panel = getPanel(tab, panels[idx++]);
    attr(tab, ARIA_SELECTED, `${tab === selected}`);
    attr(tab, TABINDEX, tab === selected ? "0" : "-1");
    if (panel) syncPanel(panel, tab, panel === nextPanel);
  }
});
customElements.define("u-tabs", UHTMLTabsElement);
customElements.define("u-tablist", UHTMLTabListElement);
customElements.define("u-tab", UHTMLTabElement);
customElements.define("u-tabpanel", UHTMLTabPanelElement);

export { UHTMLTabElement, UHTMLTabListElement, UHTMLTabListShadowRoot, UHTMLTabListStyle, UHTMLTabPanelElement, UHTMLTabPanelShadowRoot, UHTMLTabPanelStyle, UHTMLTabShadowRoot, UHTMLTabStyle, UHTMLTabsElement, UHTMLTabsShadowRoot, UHTMLTabsStyle };
